import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { MessageSquare } from "lucide-react";
import { format } from "date-fns";

interface ChatAnalysis {
  id: string;
  file_name: string;
  message_count: number;
  created_at: string;
}

interface ChatSelectorProps {
  onSelect: (chatIds: string[]) => void;
}

const ChatSelector = ({ onSelect }: ChatSelectorProps) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [chats, setChats] = useState<ChatAnalysis[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadChats();
  }, [user]);

  const loadChats = async () => {
    try {
      const { data, error } = await supabase
        .from("chat_analyses")
        .select("id, file_name, message_count, created_at")
        .eq("user_id", user!.id)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setChats(data || []);
    } catch (error) {
      console.error("Error loading chats:", error);
      toast.error(t("toast.chatHistoryFailed") || "Failed to load chats");
    } finally {
      setLoading(false);
    }
  };

  const toggleChat = (chatId: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(chatId)) {
      newSelected.delete(chatId);
    } else {
      newSelected.add(chatId);
    }
    setSelectedIds(newSelected);
  };

  const handleCompare = () => {
    if (selectedIds.size < 2) {
      toast.error(t("multiView.selectAtLeast2") || "Please select at least 2 chats");
      return;
    }
    if (selectedIds.size > 4) {
      toast.error(t("multiView.selectMaximum4") || "You can select up to 4 chats");
      return;
    }
    onSelect(Array.from(selectedIds));
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
        <p className="text-muted-foreground">{t("viewer.loading") || "Loading..."}</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            {t("multiView.selectChats") || "Select Chats to Compare"}
          </CardTitle>
          <CardDescription>
            {t("multiView.selectDescription") || "Select between 2 to 4 chats to view them side by side synchronized by time"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 mb-6">
            {chats.map((chat) => (
              <div
                key={chat.id}
                className="flex items-start gap-3 p-4 rounded-lg border hover:bg-accent/50 transition-colors cursor-pointer"
                onClick={() => toggleChat(chat.id)}
              >
                <Checkbox
                  checked={selectedIds.has(chat.id)}
                  onCheckedChange={() => toggleChat(chat.id)}
                />
                <div className="flex-1">
                  <h3 className="font-semibold">{chat.file_name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {chat.message_count} {t("viewer.messages") || "messages"} • {format(new Date(chat.created_at), "MMM d, yyyy")}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {selectedIds.size} {t("multiView.selected") || "selected"}
            </p>
            <Button
              onClick={handleCompare}
              disabled={selectedIds.size < 2 || selectedIds.size > 4}
            >
              {t("multiView.compare") || "Compare Chats"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChatSelector;
